import { Check } from 'flowbite-react-icons/outline'
import { useState } from 'react'

import { useCopyToClipboard } from '@/utils/clipboard'

interface CopyableValueProps {
  /** Raw value written to the clipboard (may differ from the displayed children). */
  copyValue: string
  /** Accessible action label, e.g. "Copy MRN" / "Copy order number". */
  ariaLabel: string
  /** Rendered display content (formatted / truncated presentation). */
  children: React.ReactNode
  /** Extra classes for the trigger button. */
  className?: string
  /** How long the "Copied" confirmation stays visible (ms). */
  resetMs?: number
}

/**
 * Click-to-copy wrapper for banner values (MRN, Order #). Renders a real
 * `<button>` for keyboard + screen-reader support, copies the raw `copyValue`
 * (not the displayed string), and shows a transient "Copied" check announced
 * via an `aria-live="polite"` region. Only render this when a real value
 * exists — callers must guard placeholder/empty states.
 *
 * A small styled tooltip (hover/focus) reads "Click to copy" by default and
 * flips to "Copied" while the copied flag is set; it hides on pointer-leave /
 * blur so the "Copied" confirmation stays only as long as the pointer/focus
 * remains on the trigger.
 */
export function CopyableValue({ copyValue, ariaLabel, children, className, resetMs = 1500 }: CopyableValueProps) {
  const { copied, copy } = useCopyToClipboard(resetMs)
  const [open, setOpen] = useState(false)

  return (
    <button
      type="button"
      onClick={() => void copy(copyValue)}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      aria-label={ariaLabel}
      className={`group relative inline-flex cursor-pointer items-center gap-1 rounded-base px-1 -mx-1 text-left transition-colors hover:bg-background-gray focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/40 dark:hover:bg-gray-800 ${className ?? ''}`}
    >
      <span>{children}</span>
      {copied ? (
        <Check className="h-3.5 w-3.5 shrink-0 text-success-600" aria-hidden />
      ) : null}
      {open ? (
        <span
          role="tooltip"
          aria-hidden
          className="pointer-events-none absolute bottom-full left-1/2 z-50 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-base bg-gray-900 px-2 py-1 text-xs font-medium text-white shadow-sm dark:bg-gray-700"
        >
          {copied ? 'Copied' : 'Click to copy'}
        </span>
      ) : null}
      <span className="sr-only" role="status" aria-live="polite">
        {copied ? 'Copied' : ''}
      </span>
    </button>
  )
}
